import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AiGovernance.css';
import AiGovNav from './AiGovNav';
import { getOrgPermissions } from '../../utils/permissions';
import { AI_FRAMEWORKS, listRisks, createRisk, deleteRisk, generateRisks } from './aiGovApi';

const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const sevClass = (s) => ({ CRITICAL: 'aig-b-crit', HIGH: 'aig-b-high', MEDIUM: 'aig-b-med', LOW: 'aig-b-low' }[s] || 'aig-b-na');

export default function AiRiskRegister() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [genFw, setGenFw] = useState(AI_FRAMEWORKS[0].id);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRisks(await listRisks()); } catch { setRisks([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => { setBusy(true); try { await generateRisks(genFw); await load(); } finally { setBusy(false); } };
  const remove = async (id) => { await deleteRisk(id); load(); };

  const counts = SEVERITIES.reduce((acc, s) => ({ ...acc, [s]: risks.filter((r) => r.severity === s).length }), {});

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('AIGovernance.riskTitle', 'AI Risk Register')}</h1>
        <p className="aig-subtitle">{t('AIGovernance.riskSubtitle', 'Risks from failing controls, high-risk AI systems and manual entries.')}</p>
      </div>

      <div className="aig-stat-bar">
        {SEVERITIES.map((s) => (
          <div className="aig-stat" key={s}>
            <div className="aig-stat-label">{s}</div>
            <div className={`aig-stat-val ${s === 'CRITICAL' || s === 'HIGH' ? 'aig-val-bad' : ''}`}>{counts[s]}</div>
          </div>
        ))}
      </div>

      <AiGovNav />

      <div className="aig-toolbar">
        <div className="aig-spacer" />
        {canManageFrameworks && (
          <>
            <select className="aig-btn" value={genFw} onChange={(e) => setGenFw(Number(e.target.value))}>
              {AI_FRAMEWORKS.map((f) => <option key={f.id} value={f.id}>{f.displayName}</option>)}
            </select>
            <button className="aig-btn" disabled={busy} onClick={generate}>{t('AIGovernance.autoGenerate', 'Auto-generate from posture')}</button>
            <button className="aig-btn aig-btn-primary" onClick={() => setShowAdd(true)}>+ {t('AIGovernance.addRisk', 'Add risk')}</button>
          </>
        )}
      </div>

      {loading ? (
        <div className="aig-spinner" />
      ) : risks.length === 0 ? (
        <div className="aig-empty">{t('AIGovernance.noRisks', 'No risks yet. Auto-generate from posture or add one manually.')}</div>
      ) : (
        <table className="aig-table">
          <thead><tr>
            <th>{t('AIGovernance.risk', 'Risk')}</th><th>{t('AIGovernance.severity', 'Severity')}</th>
            <th>{t('AIGovernance.treatment', 'Treatment')}</th><th>{t('AIGovernance.status', 'Status')}</th>
            <th>{t('AIGovernance.source', 'Source')}</th><th /></tr></thead>
          <tbody>
            {risks.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.title}</strong>{r.description && <div className="aig-muted" style={{ fontSize: '0.72rem' }}>{r.description}</div>}</td>
                <td><span className={`aig-badge ${sevClass(r.severity)}`}>{r.severity}</span></td>
                <td>{r.treatment || '—'}</td>
                <td><span className="aig-badge aig-b-na">{r.status}</span></td>
                <td>{r.source}</td>
                <td>{canManageFrameworks && <button className="aig-btn" onClick={() => remove(r.id)}>{t('AIGovernance.delete', 'Delete')}</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && <AddRiskModal t={t} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddRiskModal({ t, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', severity: 'MEDIUM', treatment: 'MITIGATE' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try { await createRisk(form); onSaved(); } catch { setSaving(false); }
  };
  return (
    <div className="aig-overlay" onClick={onClose}>
      <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('AIGovernance.addRisk', 'Add risk')}</h3>
        <div className="aig-field"><label>{t('AIGovernance.risk', 'Title')}</label>
          <input value={form.title} onChange={(e) => set('title', e.target.value)} /></div>
        <div className="aig-field"><label>{t('AIGovernance.description', 'Description')}</label>
          <textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
        <div className="aig-field"><label>{t('AIGovernance.severity', 'Severity')}</label>
          <select value={form.severity} onChange={(e) => set('severity', e.target.value)}>{SEVERITIES.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="aig-field"><label>{t('AIGovernance.treatment', 'Treatment')}</label>
          <select value={form.treatment} onChange={(e) => set('treatment', e.target.value)}>{['MITIGATE', 'ACCEPT', 'TRANSFER', 'AVOID'].map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="aig-modal-actions">
          <button className="aig-btn" onClick={onClose}>{t('AIGovernance.cancel', 'Cancel')}</button>
          <button className="aig-btn aig-btn-primary" disabled={saving || !form.title} onClick={submit}>{t('AIGovernance.save', 'Save')}</button>
        </div>
      </div>
    </div>
  );
}
